/**
 * Currency amount entry — the single source of truth for parsing, sanitizing and
 * formatting a user-typed payment amount, shared by the legacy `OtherAmountView`
 * and the v1 other-amount screen (PYMT-2287).
 *
 * The behaviour is intentionally identical to the original legacy implementation:
 * separators and decimal support are DERIVED at runtime from the app's own
 * `vue-i18n` currency formatter (`n(value, "currency", locale)`) rather than a
 * hardcoded per-currency table, so whatever `updateCurrencySymbol` configures for
 * the device country flows through unchanged (e.g. COP/MXN forced to 2 decimals).
 *
 * Callers pass a `formatCurrency` closure — bind it to `n(v, "currency", locale)`
 * — which keeps this module free of any framework dependency and unit-testable.
 */

export const MAX_DECIMAL_DIGITS = 2;

export interface CurrencyMeta {
  /** Whether the currency renders a minor unit (false for e.g. IDR, which rounds). */
  allowsDecimals: boolean;
  thousandSeparator: "." | ",";
  decimalSeparator: "." | ",";
}

/** A currency formatter, e.g. `(v) => n(v, "currency", locale)`. */
export type FormatCurrency = (value: number) => string;

/**
 * Derive the separators and decimal support from the locale's currency format,
 * mirroring the legacy detection exactly:
 * - decimals allowed when 1.5 renders a separator followed by a digit,
 * - thousand/decimal separators inferred from formatting 1000 / 1.1.
 */
export function deriveCurrencyMeta(formatCurrency: FormatCurrency): CurrencyMeta {
  return {
    allowsDecimals: /[.,]\d/.test(formatCurrency(1.5)),
    thousandSeparator: formatCurrency(1000).includes(".") ? "." : ",",
    decimalSeparator: formatCurrency(1.1).includes(".") ? "." : ",",
  };
}

/**
 * Derive the currency symbol by stripping digits, separators and spaces from a
 * formatted sample — the string `formatSanitizedAmount` strips before display.
 */
export function deriveCurrencySymbol(formatCurrency: FormatCurrency): string {
  return formatCurrency(0).replace(/[\d.,\s ]/g, "");
}

/**
 * Convert a display/typed currency string to its numeric value (e.g. "1.234,56"
 * → 1234.56). Identical to the legacy `currencyToNumber`: keep only digits and
 * the decimal separator, then normalise the separator to a JS decimal point.
 */
export function currencyToNumber(amountString: string, decimalSeparator: string): number {
  const numericValueString = amountString.replace(new RegExp(`[^0-9${decimalSeparator}]`, "g"), "");
  return Number(numericValueString.replace(decimalSeparator, "."));
}

/**
 * Reduce a raw input string to a valid, still-being-typed amount: strip invalid
 * characters (rejecting the decimal separator entirely for no-minor-unit
 * currencies), collapse repeated decimal separators, and truncate to two decimal
 * places. `pending` is true when the string ends with the decimal separator — the
 * user is mid-way through typing a decimal, so it should be shown verbatim and not
 * yet formatted or validated.
 */
export function sanitizeAmount(raw: string, meta: CurrencyMeta): { sanitized: string; pending: boolean } {
  const { allowsDecimals, thousandSeparator, decimalSeparator } = meta;
  const decimalCharForRegex = allowsDecimals ? decimalSeparator : "";
  let sanitized = raw.replace(new RegExp(`[^0-9${decimalCharForRegex}${thousandSeparator}]`, "g"), "");

  // Collapse multiple decimal separators — keep only the first (e.g. "1,,2" → "1,2").
  const decParts = sanitized.split(decimalSeparator);
  if (decParts.length > 2) {
    sanitized = `${decParts[0]}${decimalSeparator}${decParts.slice(1).join("")}`;
  }

  if (sanitized.endsWith(decimalSeparator)) {
    return { sanitized, pending: true };
  }

  // Truncate to 2 decimal places so a third digit can't round the value up.
  if (sanitized.includes(decimalSeparator)) {
    const [intPart, decPart] = sanitized.split(decimalSeparator);
    if (decPart && decPart.length > MAX_DECIMAL_DIGITS) {
      sanitized = `${intPart}${decimalSeparator}${decPart.substring(0, MAX_DECIMAL_DIGITS)}`;
    }
  }

  return { sanitized, pending: false };
}

/**
 * Apply one in-webview keypad press (`"0"`–`"9"`, `"decimal"` or `"backspace"`) to the
 * raw typed amount. Digits past `maxLength` or past `MAX_DECIMAL_DIGITS` decimals are
 * ignored, so the raw value never holds digits the display doesn't show.
 */
export function applyKeypadKey(raw: string, key: string, meta: CurrencyMeta, maxLength: number): string {
  if (key === "backspace") return raw.slice(0, -1);
  const { allowsDecimals, decimalSeparator } = meta;
  if (key === "decimal") {
    if (!allowsDecimals || raw.includes(decimalSeparator)) return raw;
    return `${raw || "0"}${decimalSeparator}`;
  }
  if (raw.length >= maxLength) return raw;
  const separatorIndex = raw.indexOf(decimalSeparator);
  if (allowsDecimals && separatorIndex !== -1 && raw.length - separatorIndex - 1 >= MAX_DECIMAL_DIGITS) return raw;
  return `${raw}${key}`;
}

/**
 * Format a sanitized (non-pending) amount for display, preserving the user's typed
 * decimals rather than letting the formatter append trailing zeros. Returns the
 * symbol-stripped display string and the numeric value (`null` when not a number).
 */
export function formatSanitizedAmount(
  sanitized: string,
  meta: CurrencyMeta,
  formatCurrency: FormatCurrency,
  symbol: string,
): { display: string; value: number | null } {
  const { decimalSeparator } = meta;
  const value = currencyToNumber(sanitized, decimalSeparator);
  if (isNaN(value)) {
    return { display: "", value: null };
  }

  const withoutSymbol = formatCurrency(value).replace(symbol, "").trim();

  // Preserve the user's decimal digits as typed (don't auto-append zeros).
  if (sanitized.includes(decimalSeparator)) {
    const [, userDec] = sanitized.split(decimalSeparator);
    const intFormatted = withoutSymbol.split(decimalSeparator)[0];
    return { display: `${intFormatted}${decimalSeparator}${userDec}`, value };
  }

  return { display: withoutSymbol.split(decimalSeparator)[0], value };
}
